import { Button as MuiButton, Typography } from '@material-ui/core';
import { useField } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { PricePlanFormContent } from 'app/pages/Registration/interfaces';
import PricePlanOption from 'app/components/PricePlanOption';

const Billing = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  font-weight: 300;
  padding: 0 0.63rem;
`;
const StyledMuiButton = styled(MuiButton)<{ $selected: boolean }>`
  max-width: 21rem;

  padding: 1rem;
  font-size: 14px;
  .MuiButton-label {
    align-items: flex-end;
  }
  .MuiButton-label::before {
    left: 0;
    width: 100%;
    min-width: 1rem;
    max-width: 1rem;
    aspect-ratio: 1/1;
    margin-right: 0.75rem;
    ${({
      $selected,
      theme: {
        palette: {
          primary: { main },
        },
      },
    }) =>
      css`
        border: ${$selected ? '0.30rem' : '0.125rem'} solid
          ${$selected ? 'white' : main};
      `}

    border-radius: 100px;
    content: '';
  }
`;

const Button: React.FC<{ onClick: () => void; selected: boolean }> = ({
  selected,
  children,
  ...props
}) => (
  <StyledMuiButton
    variant={selected ? 'contained' : 'outlined'}
    fullWidth
    color="primary"
    $selected={selected}
    {...props}
  >
    {children}
  </StyledMuiButton>
);
interface Props {
  children?: never;
  path: string;
  options: PricePlanFormContent['options'];
}

const usePeriod = (path: string) => {
  const [
    { value: period },
    ,
    { setValue: setPeriod, setTouched: setPeriodTouched },
  ] = useField(`${path}.pricePlan.period`);

  useEffect(() => {
    setPeriodTouched(true);
    // trigger only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [period, setPeriod];
};

const PricePlan: React.FC<Props> = ({ options, path }) => {
  const { t } = useTranslation();
  const [
    { value: plan },
    { error, touched },
    { setValue: setPlan, setTouched: setPlanTouched },
  ] = useField(`${path}.pricePlan.plan`);
  const [period, setPeriod] = usePeriod(path);

  return (
    <>
      <Billing>
        <Button
          onClick={() => setPeriod('monthly')}
          selected={period === 'monthly'}
        >
          {t('registration.component.pricePlan.monthly')}
        </Button>
        <Button
          onClick={() => setPeriod('annual')}
          selected={period === 'annual'}
        >
          {t(`registration.component.pricePlan.annual`)}
        </Button>
      </Billing>
      <Typography className="form-field__max-width" variant="h6" align="center">
        {t('registration.component.pricePlan.info')}
      </Typography>
      {options?.map(({ yearPrice, monthPrice, perks, title }, i) => (
        <PricePlanOption
          key={title}
          error={error !== undefined && touched}
          title={title}
          period={period}
          monthPrice={monthPrice}
          perks={perks}
          yearPrice={yearPrice}
          selected={i === plan}
          onClick={() => {
            setPlanTouched(true);
            setPlan(i, true);
          }}
        />
      ))}
    </>
  );
};

export default PricePlan;
